import { baseApiUrl, fetcher, handleError, ServerErrorResponse } from 'data_sources/constants';
import { CreateDTBPackageRequest, DTBPackage, DTBPackageJurisdiction } from 'views/App/Portal/DTB/_types/dtb';

export type UpdateDTBPackageRequest = Omit<DTBPackage, 'manual' | 'jurisdiction'> & {
  manual: { id: string; name: string; } | null;
  jurisdiction: DTBPackageJurisdiction | null;
};

export default function useManageDTBPackages() {
  const create = async (
    orgId: string,
    data: CreateDTBPackageRequest,
    callback?: (data: DTBPackage) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    if (!orgId) throw new Error('No organization ID provided.');
    const url = `${baseApiUrl()}/lexiverse/dtbs/v1/organizations/${orgId}/packages`;
    await fetcher({
      url,
      method: 'POST',
      body: data,
    })
      .then((response) => {
        if (callback) callback(response);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const update = async (
    orgId: string,
    dtbPackageId: string,
    data: UpdateDTBPackageRequest,
    callback?: (data: DTBPackage) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    if (!orgId) throw new Error('No organization ID provided.');
    const url = `${baseApiUrl()}/lexiverse/dtbs/v1/organizations/${orgId}/packages/${dtbPackageId}`;
    await fetcher({
      url,
      method: 'PUT',
      body: data,
    })
      .then((response) => {
        if (callback) callback(response);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const remove = async (
    orgId: string,
    dtbPackageId: string,
    callback?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    if (!orgId) throw new Error('No organization ID provided.');
    const url = `${baseApiUrl()}/lexiverse/dtbs/v1/organizations/${orgId}/packages/${dtbPackageId}`;
    try {
      await fetcher({
        url,
        method: 'DELETE',
        body: {},
      });
      if (callback) callback();
    } catch (err) {
      if (onError) handleError(err as Error, { onError });
      throw err;
    }
  };

  const addDtbs = async (
    orgId: string,
    dtbPackageId: string,
    dtbs: { id: string; }[],
    callback?: (data: DTBPackage) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    if (!orgId) throw new Error('No organization ID provided.');
    const url = `${baseApiUrl()}/lexiverse/dtbs/v1/organizations/${orgId}/packages/${dtbPackageId}/dtbs`;
    const body = { dtbs };
    await fetcher({
      url,
      method: 'POST',
      body,
    })
      .then((response) => {
        if (callback) callback(response);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const removeDtbs = async (
    orgId: string,
    dtbPackageId: string,
    dtbs: { id: string; }[],
    callback?: (data: DTBPackage) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    if (!orgId) throw new Error('No organization ID provided.');
    const url = `${baseApiUrl()}/lexiverse/dtbs/v1/organizations/${orgId}/packages/${dtbPackageId}/dtbs`;
    const body = { dtbs };
    await fetcher({
      url,
      method: 'DELETE',
      body,
    })
      .then((response) => {
        if (callback) callback(response);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const approveDtbs = async (
    orgId: string,
    userId: string,
    dtbs: { id: string; }[],
    callback?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    if (!orgId) throw new Error('No organization ID provided.');
    const url = `${baseApiUrl()}/lexiverse/dtbs/v1/organizations/${orgId}/approve-dtbs`;
    const body = {
      userId,
      orgId,
      dtbs,
    };
    await fetcher({
      url,
      method: 'POST',
      body,
    })
      .then(() => {
        if (callback) callback();
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const approvePackage = async (
    orgId: string,
    dtbPackageId: string,
    callback?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    if (!orgId) throw new Error('No organization ID provided.');
    if (!dtbPackageId) throw new Error('No package ID provided.');
    const url = `${baseApiUrl()}/lexiverse/dtbs/v1/organizations/${orgId}/packages/${dtbPackageId}/approve-change`;
    try {
      const response = await fetcher({
        url,
        method: 'POST',
        body: {},
      });
      if (response && (response.httpStatus >= 400 || response.errorType)) {
        throw new Error(response.message || 'Failed to approve package');
      }
      if (callback) callback();
      return response;
    } catch (err) {
      if (onError) handleError(err as Error, { onError });
      throw err;
    }
  };

  const setDtbsFlags = async (
    orgId: string,
    dtbs: { id: string; clearanceFlag: string; }[],
    callback?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    if (!orgId) throw new Error('No organization ID provided.');
    const url = `${baseApiUrl()}/lexiverse/dtbs/v1/organizations/${orgId}/set-dtb-flags`;
    const body = { dtbs };
    try {
      const response = await fetcher({
        url,
        method: 'POST',
        body,
      });
      if (response && (response.httpStatus >= 400 || response.errorType)) {
        throw new Error(response.message || 'Failed to set DTB flags');
      }
      if (callback) callback();
      return response;
    } catch (err) {
      if (onError) handleError(err as Error, { onError });
      throw err;
    }
  };

  return {
    create,
    update,
    remove,
    addDtbs,
    removeDtbs,
    approveDtbs,
    approvePackage,
    setDtbsFlags,
  };
}
